import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReminderService } from './reminder.service';
import { ToastService } from '../../services/toast.service';

type ReminderCategoryApiItem = {
  _id: string;
  habitName: string;
  description?: string;
  isActive?: boolean;
  subCategories?: Array<{
    name: string;
    predefinedReminderNames?: Array<{
      nameId: number;
      categoryName: string;
      description?: string;
      icon?: string;
      frequency?: string;
      time?: string;
    }>;
  }>;
};

type ReminderNameOption = { nameId: number; label: string };

type HabitRecommendationsApiResponse = {
  careTeamHabitRecommendations?: Array<{
    habitNameId: string;
    name: string;
    description?: string;
    icon?: string;
    isActive?: boolean;
    subCategories?: Array<{
      name: string;
      predefinedReminder?: Array<{
        nameId: number;
        name: string;
        description?: string;
        icon?: string;
        frequency?: string;
        time?: string;
        screenName?: string;
        isUsed?: boolean;
        modifiedDate?: string;
        modifiedDateLocal?: string;
      }>;
    }>;
  }>;
  healdHabitRecommendations?: Array<{
    habitNameId: string;
    name: string;
    description?: string;
    icon?: string;
    isActive?: boolean;
    subCategories?: Array<{
      name: string;
      predefinedReminder?: Array<{
        nameId: number;
        name: string;
        description?: string;
        icon?: string;
        frequency?: string;
        time?: string;
        screenName?: string;
        isUsed?: boolean;
        modifiedDate?: string;
        modifiedDateLocal?: string;
      }>;
    }>;
  }>;
  message?: string;
};

type HabitRecommendationCardRow = {
  habitNameId: string;
  habitName: string;
  nameId: number;
  categoryName: string;
  modifiedDate: string | null;
  isUsed: boolean;
};

@Component({
  selector: 'app-reminder',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reminder.component.html',
  styleUrl: './reminder.component.css'
})
export class ReminderComponent implements OnInit {
  @Input() memberId: string = '';

  activeSubTab: 'health' | 'careTeam' = 'health';

  reminderCategoriesResponse: any;
  reminderCategories: ReminderCategoryApiItem[] = [];
  isLoadingReminderCategories = false;

  habitRecommendationsResponse: HabitRecommendationsApiResponse | null = null;
  isLoadingHabitRecommendations = false;
  healdRecommendationCards: HabitRecommendationCardRow[] = [];
  careTeamRecommendationCards: HabitRecommendationCardRow[] = [];

  get habitItems(): Array<{ id: string; name: string }> {
    return (this.reminderCategories ?? [])
      .map((x) => ({ id: x?._id, name: (x?.habitName ?? '').trim() }))
      .filter((x) => Boolean(x.id) && Boolean(x.name));
  }

  constructor(
    private reminderService: ReminderService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    // Component is created only when Habits tab is opened (via *ngIf in parent),
    // so this will hit the API whenever the user navigates to Habits.
    this.isLoadingReminderCategories = true;
    this.reminderService.getReminderCategories().subscribe({
      next: (res) => {
        this.reminderCategoriesResponse = res;
        const normalized = (res as any)?.result ?? res;
        this.reminderCategories = Array.isArray(normalized) ? normalized : [];
        this.isLoadingReminderCategories = false;
      },
      error: () => {
        this.reminderCategories = [];
        this.isLoadingReminderCategories = false;
      },
    });
    this.loadHabitRecommendations();
  }

  // Health recommendations selections
  selectedHabitHealthId: string | null = null;
  selectedHealthNameId: number | null = null;
  selectedCardColorHealth: string | null = null;

  // Care team recommendations selections
  selectedHabitCareTeamId: string | null = null;
  selectedCareTeamNameId: number | null = null;
  selectedCardColorCareTeam: string | null = null;

  readonly cardColors: string[] = ['Yellow', 'Blue', 'Brown', 'Pink', 'Red'];

  get healthNameOptions(): ReminderNameOption[] {
    return this.getNameOptionsForHabit(this.selectedHabitHealthId);
  }

  get careTeamNameOptions(): ReminderNameOption[] {
    return this.getNameOptionsForHabit(this.selectedHabitCareTeamId);
  }

  setSubTab(tab: 'health' | 'careTeam') {
    this.activeSubTab = tab;
  }

  onHabitChangeHealth() {
    this.selectedHealthNameId = null;
    this.selectedCardColorHealth = null;
  }

  onHabitChangeCareTeam() {
    this.selectedCareTeamNameId = null;
    this.selectedCardColorCareTeam = null;
  }

  addHealthRecommendation() {
    if (this.isLoadingHabitRecommendations) {
      this.toast.errorMessage('Loading recommendations, please try again.');
      return;
    }
    if ((this.healdRecommendationCards?.length ?? 0) >= 5) {
      this.toast.errorMessage('We can only add 5 habit , So you have reached the limit');
      return;
    }

    if (
      !this.selectedHabitHealthId ||
      this.selectedHealthNameId == null ||
      !this.selectedCardColorHealth
    )
      return;

    const isDuplicate = (this.healdRecommendationCards ?? []).some(
      (x) => x.habitNameId === this.selectedHabitHealthId && x.nameId === this.selectedHealthNameId
    );
    if (isDuplicate) {
      this.toast.errorMessage('This recommendation already present');
      return;
    }

    const payload = {
      loggedInUserId: this.reminderService.getLoggedInUserId(),
      habitNameId: this.selectedHabitHealthId,
      nameId: this.selectedHealthNameId,
      cardColour: this.selectedCardColorHealth,
    };

    this.reminderService.addHealthRecommendation(payload).subscribe({
      next: (res) => {
        this.selectedHabitHealthId = null;
        this.selectedHealthNameId = null;
        this.selectedCardColorHealth = null;
        this.toast.successMessage(this.getApiMessage(res) ?? 'Added successfully.');
        this.loadHabitRecommendations();
      },
      error: (err) => {
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Something went wrong. Please try again.');
      },
    });
  }

  addCareTeamRecommendation() {
    if (this.isLoadingHabitRecommendations) {
      this.toast.errorMessage('Loading recommendations, please try again.');
      return;
    }
    if ((this.careTeamRecommendationCards?.length ?? 0) >= 5) {
      this.toast.errorMessage('We can only add 5 habit , So you have reached the limit');
      return;
    }

    if (
      !this.selectedHabitCareTeamId ||
      this.selectedCareTeamNameId == null ||
      !this.selectedCardColorCareTeam
    )
      return;

    const isDuplicate = (this.careTeamRecommendationCards ?? []).some(
      (x) => x.habitNameId === this.selectedHabitCareTeamId && x.nameId === this.selectedCareTeamNameId
    );
    if (isDuplicate) {
      this.toast.errorMessage('This recommendation already present');
      return;
    }

    const payload = {
      loggedInUserId: this.reminderService.getLoggedInUserId(),
      memberId: this.memberId,
      habitNameId: this.selectedHabitCareTeamId,
      nameId: this.selectedCareTeamNameId,
      cardColour: this.selectedCardColorCareTeam,
    };

    this.reminderService.addCareTeamRecommendation(payload).subscribe({
      next: (res) => {
        this.selectedHabitCareTeamId = null;
        this.selectedCareTeamNameId = null;
        this.selectedCardColorCareTeam = null;
        this.toast.successMessage(this.getApiMessage(res) ?? 'Added successfully.');
        this.loadHabitRecommendations();
      },
      error: (err) => {
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Something went wrong. Please try again.');
      },
    });
  }

  private getApiMessage(res: any): string | null {
    const msg =
      res?.message ??
      res?.result?.message ??
      res?.error?.message ??
      res?.error?.result?.message ??
      res?.error?.title;
    return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
  }

  private getNameOptionsForHabit(habitId: string | null): ReminderNameOption[] {
    if (!habitId) return [];
    const habit = (this.reminderCategories ?? []).find((x) => x?._id === habitId);
    const raw =
      habit?.subCategories?.flatMap((sc) =>
        (sc?.predefinedReminderNames ?? []).map((n) => ({
          nameId: n?.nameId,
          label: (n?.categoryName ?? '').trim(),
        }))
      ) ?? [];

    const options = raw.filter(
      (x) => typeof x.nameId === 'number' && Number.isFinite(x.nameId) && Boolean(x.label)
    ) as ReminderNameOption[];

    // Deduplicate by nameId while preserving order
    const seen = new Set<number>();
    return options.filter((x) => {
      if (seen.has(x.nameId)) return false;
      seen.add(x.nameId);
      return true;
    });
  }

  private loadHabitRecommendations(): void {
    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) return;

    this.isLoadingHabitRecommendations = true;
    this.reminderService.getHabitRecommendations(mid).subscribe({
      next: (res) => {
        const normalized = (res as any)?.result ?? res;
        this.habitRecommendationsResponse = normalized as HabitRecommendationsApiResponse;
        this.healdRecommendationCards = this.flattenHabitRecommendations(
          this.habitRecommendationsResponse?.healdHabitRecommendations ?? []
        );
        this.careTeamRecommendationCards = this.flattenHabitRecommendations(
          this.habitRecommendationsResponse?.careTeamHabitRecommendations ?? []
        );
        this.isLoadingHabitRecommendations = false;
      },
      error: (err) => {
        this.habitRecommendationsResponse = null;
        this.healdRecommendationCards = [];
        this.careTeamRecommendationCards = [];
        this.isLoadingHabitRecommendations = false;
        const msg = this.getApiMessage(err);
        if (msg) this.toast.errorMessage(msg);
      },
    });
  }

  private flattenHabitRecommendations(
    habits: NonNullable<HabitRecommendationsApiResponse['healdHabitRecommendations']>
  ): HabitRecommendationCardRow[] {
    const rows =
      habits.flatMap((habit) => {
        const habitNameId = (habit?.habitNameId ?? '').toString().trim();
        const habitName = (habit?.name ?? '').trim();
        const subs = habit?.subCategories ?? [];
        return subs.flatMap((sc) => {
          const items = sc?.predefinedReminder ?? [];
          return items.map((p) => {
            const nameId = Number(p?.nameId);
            const categoryName = (p?.name ?? '').trim();
            const modifiedDate = (p?.modifiedDateLocal ?? p?.modifiedDate ?? null) as string | null;
            const isUsed = Boolean(p?.isUsed);
            return { habitNameId, habitName, nameId, categoryName, modifiedDate, isUsed };
          });
        });
      }) ?? [];

    return rows.filter(
      (r) =>
        Boolean(r.habitNameId) &&
        Boolean(r.habitName) &&
        Number.isFinite(r.nameId) &&
        Boolean(r.categoryName)
    );
  }
}
